import { Calendar03Icon } from '@hugeicons/core-free-icons';
import { useId } from 'react';
import { Icon } from '@/components/primitives/icon';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface DatePickerProps {
  label: string;
  /** `YYYY-MM-DD`, or `YYYY-MM-DDTHH:mm` with granularity "minute"; empty for none. */
  value?: string;
  /** Called with `{ target: { value } }` in the same formats; empty when cleared. */
  onChange?: (event: { target: { value: string } }) => void;
  granularity?: 'day' | 'minute';
  disabled?: boolean;
  className?: string;
}

const DAY = /^(\d{4})-(\d{2})-(\d{2})/;

function toDate(value: string | undefined): Date | undefined {
  const match = value ? DAY.exec(value) : null;
  if (!match) return undefined;
  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
  );
  return Number.isNaN(date.getTime()) ? undefined : date;
}

const pad = (n: number) => String(n).padStart(2, '0');

function dayText(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * A day (and, for "minute", a time) behind the props of a native date input,
 * so a screen keeps a string state and an `event.target.value` handler.
 */
export function DatePicker({
  label,
  value,
  onChange,
  granularity = 'day',
  disabled,
  className,
}: Readonly<DatePickerProps>) {
  const id = useId();
  const selected = toDate(value);
  const time = granularity === 'minute' && value ? value.slice(11, 16) : '';
  const emit = (day: string, clock: string) => {
    let next = day;
    if (!day) next = '';
    else if (granularity === 'minute') next = `${day}T${clock || '00:00'}`;
    onChange?.({ target: { value: next } });
  };

  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      <Label htmlFor={id}>{label}</Label>
      <div className="flex gap-2">
        <Popover>
          <PopoverTrigger
            render={
              <Button
                id={id}
                variant="outline"
                disabled={disabled}
                className="min-w-0 flex-1 justify-between font-normal"
              />
            }
          >
            <span className={cn(!selected && 'text-muted-foreground')}>
              {selected ? dayText(selected) : 'Pick a date'}
            </span>
            <Icon icon={Calendar03Icon} size={16} />
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={selected}
              defaultMonth={selected}
              onSelect={(next) => emit(next ? dayText(next) : '', time)}
            />
          </PopoverContent>
        </Popover>
        {granularity === 'minute' ? (
          <Input
            type="time"
            aria-label={`${label}, time`}
            className="w-28"
            disabled={disabled || !selected}
            value={time}
            onChange={(event) =>
              emit(selected ? dayText(selected) : '', event.target.value)
            }
          />
        ) : null}
      </div>
    </div>
  );
}
